/**
 * Per-member engagement metrics for the CRM ClientOverview ▸ Access tab ("Team Engagement").
 *
 * Inputs are what the integrations already give the CRM:
 *   - Gmail headers from every connected staff mailbox (fetchGmailMessages, format 'metadata')
 *   - Google Calendar events from every connected staff calendar (fetchGoogleCalendarEvents)
 *   - Fathom meeting_summaries ({ inviteeEmails, scheduledStartTime })
 *   - Portal activity rows (logins + page views, built by ClientOverview)
 *
 * Everything here is pure so the math can be tested without Firebase.
 */

import { canonicalCalendarEmailKey } from './clientCalendarEventMatching';
import { contactEmailAliasEntries, normalizeEmail, PRIMARY_CONTACT_KEY } from './contactEmailAliases';

export const ENGAGEMENT_TIMEFRAMES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
];

/** A reply later than this is treated as a new conversation, not a response. */
export const MAX_RESPONSE_MS = 14 * 24 * 60 * 60 * 1000;

/** Shared mail hosts — never treated as "our" domain even if a staff member uses one. */
const PUBLIC_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com',
  'icloud.com', 'me.com', 'aol.com', 'msn.com', 'protonmail.com', 'proton.me',
]);

const EMAIL_IN_TEXT_RE = /[^\s<>"',;()]+@[^\s<>"',;()]+\.[^\s<>"',;()]+/g;

/** Every email address in a header value ("Doe, Jane" <jane@x.com>, bob@y.com). */
export function extractEmailAddresses(value) {
  return (String(value || '').match(EMAIL_IN_TEXT_RE) || []).map(normalizeEmail);
}

/** The first email address in a header value, lowercase ('' when none). */
export function extractEmailAddress(value) {
  return extractEmailAddresses(value)[0] || '';
}

/** "Re: Fwd: Kickoff  notes" → "kickoff notes" — groups one conversation across mailboxes. */
export function normalizeSubject(subject) {
  return String(subject || '')
    .replace(/^\s*((re|fw|fwd|aw)\s*(\[\d+\])?\s*:\s*)+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Raw addresses (for Gmail search) and canonical keys (for matching) one roster member uses:
 * their portal email + alternate emails + contact aliases filed under them. The primary member
 * also owns the CRM primary contact email and the primary-contact aliases.
 */
export function memberEmailIdentity(member, account) {
  const addresses = new Set();
  const add = (email) => {
    const e = normalizeEmail(email);
    if (e && e.includes('@')) addresses.add(e);
  };
  add(member?.email);
  (member?.alternateEmails || []).forEach(add);
  if (member?.isPrimary) add(account?.contact?.email);
  contactEmailAliasEntries(account).forEach((alias) => {
    const key = alias.contactKey || PRIMARY_CONTACT_KEY;
    if (key === member?.id || (member?.isPrimary && key === PRIMARY_CONTACT_KEY)) add(alias.email);
  });
  const list = [...addresses];
  return { addresses: list, keys: new Set(list.map(canonicalCalendarEmailKey).filter(Boolean)) };
}

/**
 * Roster for the panel: every member with an email, plus a stand-in for the CRM primary
 * contact when no roster member is the primary (clients without a portal account).
 */
export function engagementRoster(account, members = []) {
  const withEmail = (members || []).filter((m) => normalizeEmail(m?.email));
  const primaryEmail = normalizeEmail(account?.contact?.email);
  const hasPrimary = withEmail.some(
    (m) => m.isPrimary || (primaryEmail && normalizeEmail(m.email) === primaryEmail)
  );
  if (hasPrimary || !primaryEmail) return withEmail;
  const contactName = [account?.contact?.firstName, account?.contact?.lastName].filter(Boolean).join(' ')
    || account?.contact?.name
    || primaryEmail;
  return [
    { id: PRIMARY_CONTACT_KEY, name: contactName, email: primaryEmail, isPrimary: true, alternateEmails: [] },
    ...withEmail,
  ];
}

/** Matchers for "is this address one of us": staff emails + staff company domains. */
export function buildInternalMatcher(internalUsers = []) {
  const keys = new Set();
  const domains = new Set();
  (internalUsers || []).forEach((u) => {
    const e = normalizeEmail(u?.email);
    if (!e) return;
    keys.add(canonicalCalendarEmailKey(e));
    const domain = e.split('@')[1];
    if (domain && !PUBLIC_MAIL_DOMAINS.has(domain)) domains.add(domain);
  });
  return (email) => {
    const e = normalizeEmail(email);
    if (!e) return false;
    if (keys.has(canonicalCalendarEmailKey(e))) return true;
    return domains.has(e.split('@')[1]);
  };
}

/**
 * One copy of each email even when several staff mailboxes hold it (everyone CC'd).
 * Prefers the RFC Message-ID; falls back to sender + timestamp + subject.
 */
export function dedupeMessages(messages = []) {
  const seen = new Map();
  (messages || []).forEach((m) => {
    const mid = String(m?.messageIdHeader || '').trim().toLowerCase();
    const at = toDate(m?.date);
    const key = mid
      ? `mid:${mid}`
      : `fb:${extractEmailAddress(m?.from)}|${at ? at.getTime() : ''}|${normalizeSubject(m?.subject)}`;
    if (!seen.has(key)) seen.set(key, m);
  });
  return [...seen.values()];
}

const average = (values) => (values.length ? values.reduce((s, v) => s + v, 0) / values.length : null);
const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Email stats for one member over deduped messages.
 *   sent      — emails the member sent to us
 *   received  — emails we sent to the member (To or Cc)
 *   ourReply  — time from the member's message to our next reply in that conversation
 *   theirReply — time from our message to the member's next reply
 *   awaitingReply — conversations whose latest message is the member's, still unanswered
 * Conversations are grouped by normalized subject so a thread split across staff
 * mailboxes still lines up. Replies slower than MAX_RESPONSE_MS are not counted.
 */
export function computeMemberEmailStats(messages, memberKeys, isInternal, { since = null, now = new Date() } = {}) {
  const rows = [];
  (messages || []).forEach((m) => {
    const at = toDate(m?.date);
    if (!at || (since && at < since) || at > now) return;
    const from = extractEmailAddress(m.from);
    const fromKey = canonicalCalendarEmailKey(from);
    const recipients = extractEmailAddresses([m.to, m.cc].filter(Boolean).join(', '));
    let direction = null;
    if (memberKeys.has(fromKey)) direction = 'from_member';
    else if (isInternal(from) && recipients.some((r) => memberKeys.has(canonicalCalendarEmailKey(r)))) {
      direction = 'to_member';
    }
    if (!direction) return;
    rows.push({ at, direction, from, conversation: normalizeSubject(m.subject) || m.threadId || m.id });
  });

  const ourSamples = [];
  const theirSamples = [];
  let awaitingReply = 0;
  let awaitingSince = null;
  const staffCounts = new Map();

  const byConversation = new Map();
  rows.forEach((r) => {
    if (!byConversation.has(r.conversation)) byConversation.set(r.conversation, []);
    byConversation.get(r.conversation).push(r);
    if (r.direction === 'to_member') staffCounts.set(r.from, (staffCounts.get(r.from) || 0) + 1);
  });

  byConversation.forEach((list) => {
    list.sort((a, b) => a.at - b.at);
    let pendingFromMember = null; // oldest member message we haven't answered
    let pendingToMember = null; // oldest message of ours they haven't answered
    list.forEach((r) => {
      if (r.direction === 'from_member') {
        if (pendingToMember) {
          const gap = r.at - pendingToMember;
          if (gap <= MAX_RESPONSE_MS) theirSamples.push(gap);
          pendingToMember = null;
        }
        if (!pendingFromMember) pendingFromMember = r.at;
      } else {
        if (pendingFromMember) {
          const gap = r.at - pendingFromMember;
          if (gap <= MAX_RESPONSE_MS) ourSamples.push(gap);
          pendingFromMember = null;
        }
        if (!pendingToMember) pendingToMember = r.at;
      }
    });
    if (pendingFromMember) {
      awaitingReply += 1;
      if (!awaitingSince || pendingFromMember < awaitingSince) awaitingSince = pendingFromMember;
    }
  });

  const latest = (direction) =>
    rows.filter((r) => r.direction === direction).reduce((max, r) => (!max || r.at > max ? r.at : max), null);

  return {
    sent: rows.filter((r) => r.direction === 'from_member').length,
    received: rows.filter((r) => r.direction === 'to_member').length,
    total: rows.length,
    conversations: byConversation.size,
    avgOurReplyMs: average(ourSamples),
    medianOurReplyMs: median(ourSamples),
    ourReplies: ourSamples.length,
    avgTheirReplyMs: average(theirSamples),
    medianTheirReplyMs: median(theirSamples),
    theirReplies: theirSamples.length,
    awaitingReply,
    awaitingSince,
    lastSentAt: latest('from_member'),
    lastReceivedAt: latest('to_member'),
    staff: [...staffCounts.entries()].map(([email, count]) => ({ email, count })).sort((a, b) => b.count - a.count),
  };
}

/** Calendar events + Fathom recordings involving one member. Declined invites are not meetings. */
export function computeMemberMeetingStats(events, summaries, memberKeys, { since = null, now = new Date() } = {}) {
  let held = 0;
  let upcoming = 0;
  let declined = 0;
  let minutesMet = 0;
  let last = null;
  let next = null;

  (events || []).forEach((event) => {
    const start = toDate(event?.start);
    if (!start) return;
    const attendee = (event.attendees || []).find((a) => memberKeys.has(canonicalCalendarEmailKey(a?.email)));
    const organizedByMember = memberKeys.has(canonicalCalendarEmailKey(event.organizer?.email));
    if (!attendee && !organizedByMember) return;
    if (attendee?.responseStatus === 'declined') {
      if (!since || start >= since) declined += 1;
      return;
    }
    const end = toDate(event.end) || start;
    if (start >= now) {
      upcoming += 1;
      if (!next || start < next.at) next = { at: start, title: event.title || event.summary || 'Meeting' };
      return;
    }
    if (since && start < since) return;
    held += 1;
    const minutes = (end - start) / 60000;
    // All-day blocks are not meetings time
    if (minutes > 0 && minutes < 12 * 60) minutesMet += minutes;
    if (!last || start > last.at) last = { at: start, title: event.title || event.summary || 'Meeting' };
  });

  const recorded = (summaries || []).filter((s) => {
    if (s?.source && s.source !== 'fathom') return false;
    const at = toDate(s?.scheduledStartTime) || toDate(s?.createdAt);
    if (!at || (since && at < since) || at > now) return false;
    return (s.inviteeEmails || []).some((e) => memberKeys.has(canonicalCalendarEmailKey(e)));
  }).length;

  return { held, upcoming, declined, hoursMet: minutesMet / 60, recorded, last, next };
}

/** Logins + page views for one member from the Access tab's merged portal rows. */
export function computeMemberPortalStats(portalRows, memberId, { since = null } = {}) {
  const rows = (portalRows || []).filter((r) => r.memberId === memberId && (!since || r.at >= since));
  const views = rows.filter((r) => r.type !== 'login');
  const pageCounts = new Map();
  views.forEach((r) => {
    const label = String(r.label || '').replace(/^Viewed\s+/, '');
    if (label && label !== 'a page') pageCounts.set(label, (pageCounts.get(label) || 0) + 1);
  });
  const topPage = [...pageCounts.entries()].sort((a, b) => b[1] - a[1])[0] || null;
  return {
    logins: rows.length - views.length,
    pageViews: views.length,
    lastActiveAt: rows.reduce((max, r) => (!max || r.at > max ? r.at : max), null),
    topPage: topPage ? { label: topPage[0], count: topPage[1] } : null,
  };
}

/** Most recent touch across email (either way) and held meetings. */
export function lastContactAt(emailStats, meetingStats) {
  return [emailStats?.lastSentAt, emailStats?.lastReceivedAt, meetingStats?.last?.at]
    .filter(Boolean)
    .reduce((max, d) => (!max || d > max ? d : max), null);
}

/** 42m · 5.2h · 3.1d */
export function formatDurationShort(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const minutes = ms / 60000;
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
  const days = hours / 24;
  return `${days < 10 ? days.toFixed(1) : Math.round(days)}d`;
}

/** "today" · "yesterday" · "12 days ago" */
export function formatDaysAgo(date, now = new Date()) {
  if (!date) return 'never';
  // Calendar days, so last night's email reads "yesterday" rather than "today"
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
